// LEGACY CODE — DO NOT USE EXCEPT FOR MIGRATION
// This module exists solely to decrypt v1-format data so it can be
// re-encrypted with the new DocumentEncryptionV2 scheme.
// After Step 5.5 (scheduled after all users confirm migration),
// this entire module gets deleted.
//
// DO NOT EXPORT ENCRYPTION ROUTINES. No code path outside the migration
// service may import symbols from this file. Adding imports elsewhere will
// keep broken-by-design ciphertext being produced.

import CryptoJS from 'crypto-js';
import { Buffer } from 'buffer';
import Aes from 'react-native-aes-crypto';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Keychain from 'react-native-keychain';

/** Legacy global salt — same constant shipped in every client build.  */
const LEGACY_SALT = 'MyFinalKeyApp_Salt_2024';
const LEGACY_ITERATIONS = 100000;
const LEGACY_KEY_LEN_WORDS = 256 / 32; // 8 words = 32 bytes

const LEGACY_MASTER_KEY_KEYCHAIN = 'document_master_key';
const LEGACY_VAULT_MASTER_KEY_ASYNC = 'vault_master_key';

export interface LegacyEnvelope {
  iv: string;
  data: string;
  algorithm: 'aes-256-cbc' | string;
}

/**
 * Reproduce the legacy master-key derivation: crypto-js PBKDF2-SHA1 with a
 * single global salt and 100 000 iterations. The output is a 64-char hex
 * string (32 bytes), which is what the legacy storage wrote to disk /
 * backend / keychain.
 */
export function deriveLegacyMasterKey(secretCode: string): string {
  const derived = CryptoJS.PBKDF2(secretCode, LEGACY_SALT, {
    keySize: LEGACY_KEY_LEN_WORDS,
    iterations: LEGACY_ITERATIONS,
  });
  return derived.toString();
}

/**
 * Decrypt a legacy AES-256-CBC envelope. The stored format is a JSON string
 * of shape `{ iv, data, algorithm: "aes-256-cbc" }`. Returns plaintext as a
 * UTF-8 string (for text) or base64 (for file content — the v1 encrypt path
 * base64-encoded binary before encrypting).
 */
export async function legacyAesDecrypt(
  encryptedText: string,
  key: string,
): Promise<string> {
  let parsed: LegacyEnvelope;
  try {
    parsed = JSON.parse(encryptedText) as LegacyEnvelope;
  } catch {
    // Pre-AES-CBC blobs were stored as raw base64 XOR ciphertext (no JSON
    // wrapper). The legacy code fell back to XOR in this branch.
    return legacyXorDecrypt(encryptedText, key);
  }

  const { iv, data, algorithm } = parsed;
  if (!iv || !data) {
    throw new Error('legacyAesDecrypt: envelope missing iv or data');
  }

  if (algorithm !== 'aes-256-cbc') {
    // Unknown algorithm — legacy code treated this as XOR.
    return legacyXorDecrypt(data, key);
  }

  return Aes.decrypt(data, key, iv, 'aes-256-cbc');
}

/**
 * Legacy XOR "decrypt". Base64 → XOR against the ASCII bytes of the repeating
 * key → base64 again. Used as a fallback by `legacyAesDecrypt` when the blob
 * doesn't parse as JSON. Never call this for new data — it is not
 * cryptographically secure.
 */
export function legacyXorDecrypt(encryptedText: string, key: string): string {
  const decoded = new Uint8Array(Buffer.from(encryptedText, 'base64'));
  const result = new Uint8Array(decoded.length);
  for (let i = 0; i < decoded.length; i++) {
    result[i] = decoded[i] ^ key.charCodeAt(i % key.length);
  }
  return Buffer.from(result).toString('base64');
}

/**
 * Best-effort read of the existing legacy master key from any known storage
 * location. Returns null when none is found (e.g. a fresh install, or a user
 * who has not produced any v1-format data). Callers should treat `null` as
 * "no legacy data to migrate".
 *
 * Order: keychain backup first (most trustworthy), then AsyncStorage fallback.
 * The backend copy is not consulted here — a device that was online with
 * Vault reachable would have populated the keychain backup; a device that
 * wasn't would have the AsyncStorage fallback.
 */
export async function readStoredLegacyMasterKey(): Promise<string | null> {
  try {
    const creds = await Keychain.getInternetCredentials(
      LEGACY_MASTER_KEY_KEYCHAIN,
    );
    if (creds && typeof creds !== 'boolean' && creds.password) {
      return creds.password;
    }
  } catch {
    /* keychain unavailable — fall through */
  }
  try {
    const async = await AsyncStorage.getItem(LEGACY_VAULT_MASTER_KEY_ASYNC);
    if (async) return async;
  } catch {
    /* noop */
  }
  return null;
}
